import {useQuery} from '@apollo/client';
import * as React from 'react';

import {PER_PAGE, mesclarPorId, paginaCheia} from '../paginacao';
import {DEBOUNCE_BUSCA, RecordItem, buscaValida, dataDaMarca, marcaDoRecorde} from '../recordsList';
import {QUERY_STUDENT_RECORDS} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

interface Props {
  theme: StudentTheme;
}

interface RecordsData {
  records?: RecordItem[] | null;
}

export const RecordsScreen: React.FC<Props> = ({theme}) => {
  const [busca, setBusca] = React.useState('');
  const [buscaAplicada, setBuscaAplicada] = React.useState('');

  // Sem espera, cada tecla vira uma query no backend.
  React.useEffect(() => {
    if (!buscaValida(busca)) return undefined;

    const id = setTimeout(() => setBuscaAplicada(busca.trim()), DEBOUNCE_BUSCA);

    return () => clearTimeout(id);
  }, [busca]);

  const {data, loading, error, fetchMore, refetch} = useQuery<RecordsData>(QUERY_STUDENT_RECORDS, {
    variables: {page: 1, ...(buscaAplicada ? {search: buscaAplicada} : {})},
  });

  const [pagina, setPagina] = React.useState(1);
  const [carregandoMais, setCarregandoMais] = React.useState(false);
  const [acabou, setAcabou] = React.useState(false);

  // Busca nova recomeca a paginacao; sem isso o botao pediria a pagina 3 de
  // uma lista que voltou a ter uma pagina so.
  React.useEffect(() => {
    setPagina(1);
    setAcabou(false);
  }, [buscaAplicada]);

  const itens = data?.records ?? [];

  const carregarMais = async () => {
    if (carregandoMais || acabou) return;

    const proxima = pagina + 1;
    setCarregandoMais(true);

    try {
      const {data: novos} = await fetchMore({
        variables: {page: proxima, ...(buscaAplicada ? {search: buscaAplicada} : {})},
        updateQuery: (anterior, {fetchMoreResult}) => {
          if (!fetchMoreResult?.records?.length) return anterior;

          return {records: mesclarPorId(anterior?.records ?? [], fetchMoreResult.records)};
        },
      });

      setPagina(proxima);
      if (!paginaCheia(novos?.records ?? [], PER_PAGE)) setAcabou(true);
    } catch {
      setAcabou(true);
    } finally {
      setCarregandoMais(false);
    }
  };

  return (
    <div className="records">
      <input
        type="search"
        className="records__busca"
        style={{backgroundColor: theme.body, borderColor: theme.border, color: theme.text}}
        placeholder="Buscar exercício"
        value={busca}
        onChange={(evento) => setBusca(evento.target.value)}
        aria-label="Buscar exercício"
      />

      {loading && itens.length === 0 && <p className="student-screen__estado">Carregando…</p>}

      {error && itens.length === 0 && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar os recordes.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !error && itens.length === 0 && (
        <p className="student-screen__estado">
          {buscaAplicada ? 'Nenhum exercício encontrado.' : 'Nenhum exercício disponível.'}
        </p>
      )}

      {itens.map((item) => {
        const marca = marcaDoRecorde(item);
        const quando = dataDaMarca(item);

        return (
          <article
            key={item.id}
            className="record-card"
            style={{backgroundColor: theme.body, borderColor: theme.border}}
          >
            {item.image?.uri ? (
              <img className="record-card__imagem" src={item.image.uri} alt="" loading="lazy" />
            ) : (
              <span className="record-card__imagem record-card__imagem--vazia" style={{backgroundColor: theme.border}}>
                <i className="fa-light fa-dumbbell" aria-hidden="true" />
              </span>
            )}

            <div className="record-card__info">
              <h2 className="record-card__nome">{item.name}</h2>
              {item.category && (
                <span className="record-card__categoria" style={{color: theme.neutral}}>
                  {item.category}
                </span>
              )}
            </div>

            <div className="record-card__marca">
              {marca ? (
                <>
                  <strong style={{color: theme.primary}}>{marca}</strong>
                  {quando && <span style={{color: theme.neutral}}>{quando}</span>}
                </>
              ) : (
                <span style={{color: theme.neutral}}>Sem marca</span>
              )}
            </div>
          </article>
        );
      })}

      {!acabou && itens.length >= PER_PAGE && (
        <button
          type="button"
          className="student-app__retry cursos__mais"
          onClick={carregarMais}
          disabled={carregandoMais}
        >
          {carregandoMais ? 'Carregando…' : 'Ver mais'}
        </button>
      )}
    </div>
  );
};
